import React, { useEffect, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/a11y';
import 'swiper/css/free-mode';
import 'swiper/css/keyboard';
import 'swiper/css/mousewheel';
import { A11y, FreeMode, Keyboard, Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { RequirementsProps } from '../../../../tools/context';
import ClientSideSlideshowLightbox from '../../../ClientSideSlideshowLightbox';
import { ResponsiveImage } from '../../../ResponsiveImage';

interface TravelNightCarouselProps {
  requirements: RequirementsProps;
  images: string[];
}

const TravelNightCarousel: React.FC<TravelNightCarouselProps> = ({ requirements, images }) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [validImages, setValidImages] = useState<string[]>(images);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(-1);

  useEffect(() => {
    setValidImages(images);
    setLightboxImageIndex(-1);
  }, [images]);

  useEffect(() => {
    if (swiperRef.current) swiperRef.current.slideTo(0);
  }, [images]);

  if (!images?.length) return null;

  return (
    <div className="TravelNightCarousel">
      <Swiper
        className="NightCarousel"
        modules={[A11y, FreeMode, Keyboard, Mousewheel]}
        slidesPerView="auto"
        spaceBetween={6}
        freeMode={{
          enabled: true,
          sticky: true,
          momentumRatio: 0.2,
        }}
        breakpoints={{
          768: {
            spaceBetween: 10,
          },
        }}
        mousewheel={{
          enabled: true,
          forceToAxis: true,
          releaseOnEdges: true,
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
      >
        {validImages.map((image, index) => (
          <SwiperSlide
            key={`night-carousel-${index}`}
            className="NightCarousel__slide"
            onClick={() => setLightboxImageIndex(index)}
          >
            <ResponsiveImage
              requirements={requirements}
              src={image}
              fallback={requirements.config.fallbackImage || 'https://checkout.bivouacs.huttopia.com/fallback.webp'}
              fallback2={requirements.config.fallbackImage || 'https://checkout.bivouacs.huttopia.com/fallback.webp'}
              alt={`Travel night ${index + 1}`}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      <ClientSideSlideshowLightbox
        images={validImages?.map((imageUrl, i) => ({ src: imageUrl, alt: i.toString() }))}
        startingSlideIndex={lightboxImageIndex}
        open={lightboxImageIndex > -1}
        onClose={() => setLightboxImageIndex(-1)}
      />
    </div>
  );
};

export default TravelNightCarousel;
